/**
 * Quality Domination — central SVG filter defs.
 *
 * Mounted once at the App root as an absolutely-positioned 0x0 SVG so that
 * any element on the page can reference these filters via `filter="url(#qd-...)"`.
 *
 * Filter tuning is driven by `@qd/design-tokens` `textures` so the visual
 * system has a single source of truth.
 */

import { textures } from '@qd/design-tokens';

export const FILTER_IDS = {
  chalkRoughen: 'qd-filter-chalk-roughen',
  paperGrain: 'qd-filter-paper-grain',
  slateNoise: 'qd-filter-slate-noise',
  inkBleed: 'qd-filter-ink-bleed',
} as const;

export function SvgFilterDefs() {
  const chalk = textures.chalkStroke;
  const paper = textures.paperGrain;
  const slate = textures.slateNoise;

  return (
    <svg
      aria-hidden
      focusable="false"
      width={0}
      height={0}
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <defs>
        {/* Chalk roughen: feTurbulence + feDisplacementMap to push pixels
            sideways, simulating a chalk stroke that drags slightly off-axis. */}
        <filter
          id={FILTER_IDS.chalkRoughen}
          x="-10%"
          y="-10%"
          width="120%"
          height="120%"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency={chalk.baseFrequency}
            numOctaves={chalk.numOctaves}
            seed={4}
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={chalk.displacementScale}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>

        {/* Paper grain: high-frequency turbulence multiplied at low opacity
            over cream paper so washi-tape and stamp marks read as fibre. */}
        <filter
          id={FILTER_IDS.paperGrain}
          x="0%"
          y="0%"
          width="100%"
          height="100%"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency={paper.baseFrequency}
            numOctaves={paper.numOctaves}
            seed={2}
            result="grain"
          />
          <feColorMatrix
            in="grain"
            type="matrix"
            values="0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.08 0"
            result="grainAlpha"
          />
          <feComposite in="grainAlpha" in2="SourceGraphic" operator="in" result="grainMasked" />
          <feBlend in="SourceGraphic" in2="grainMasked" mode="multiply" />
        </filter>

        {/* Slate noise: low-frequency turbulence overlaid at 6% so the slate
            background reads as chalkboard slate instead of flat #2A2E33. */}
        <filter
          id={FILTER_IDS.slateNoise}
          x="0%"
          y="0%"
          width="100%"
          height="100%"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency={slate.baseFrequency}
            numOctaves={slate.numOctaves}
            seed={7}
            result="snoise"
          />
          <feColorMatrix
            in="snoise"
            type="matrix"
            values="0 0 0 0 0.5
                    0 0 0 0 0.5
                    0 0 0 0 0.5
                    0 0 0 0.06 0"
            result="snoiseAlpha"
          />
          <feBlend in="SourceGraphic" in2="snoiseAlpha" mode="overlay" />
        </filter>

        {/* Ink bleed: blur + dilate to simulate a wax-pencil stamp where
            pigment has bled into paper fibres at the edges. Used on
            ChalkButton and rubber-stamp text. */}
        <filter
          id={FILTER_IDS.inkBleed}
          x="-10%"
          y="-10%"
          width="120%"
          height="120%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" result="blur" />
          <feMorphology in="blur" operator="dilate" radius="0.4" result="bleed" />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="1.6"
            numOctaves={2}
            seed={5}
            result="ibnoise"
          />
          <feDisplacementMap
            in="bleed"
            in2="ibnoise"
            scale="0.8"
            xChannelSelector="R"
            yChannelSelector="G"
            result="bleedDisplaced"
          />
          <feMerge>
            <feMergeNode in="bleedDisplaced" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
}
